import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {withTranslation} from 'react-i18next';
import axios from 'axios';
import {Button} from 'primereact/button';
import {Dialog} from 'primereact/dialog';
import {Tag} from 'primereact/tag';
import {Dropdown} from 'primereact/dropdown';
import DealFormComponent from './DealFormComponent';
import TaskFormComponent from '../task/TaskFormComponent';
import {useHistory} from 'react-router-dom';
import {formatCrmDateTime} from '../crmFormatters';

const OpportunityKanbanComponent = ({t}) => {
  const history = useHistory();
  const [deals, setDeals] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [pipelineId, setPipelineId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({visible: false, dealId: null});
  const [taskDialog, setTaskDialog] = useState(false);
  const headers = useMemo(() => ({Authorization: 'Bearer ' + localStorage.getItem('userToken'), 'App-Network': localStorage.getItem('networkId')}), []);
  const canWrite = (localStorage.getItem('roles') || '').toLowerCase().includes('role_crm_deal_write');
  const canWriteTask = (localStorage.getItem('roles') || '').toLowerCase().includes('role_crm_task_write');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(process.env.REACT_APP_API_HOST + '/api/v2/crm/workspace', {headers}),
      axios.get(process.env.REACT_APP_API_HOST + '/api/v2/crm-pipeline?item_per_page=200', {headers})
    ])
      .then(([workspaceResponse, pipelineResponse]) => {
        setDeals(workspaceResponse.data.data.deals || []);
        const available = (pipelineResponse.data.data.items || []).filter(item => item.active);
        setPipelines(available);
        setPipelineId(current => current || available.find(item => item.default)?.id || available[0]?.id || null);
      })
      .catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response))
      .finally(() => setLoading(false));
  }, [headers, t]);
  useEffect(() => load(), [load]);

  const move = (deal, stage) => {
    if (!canWrite || Number(deal.stage_id) === Number(stage.id)) return;
    let lostReason = deal.lost_reason;
    if (stage.category === 'LOST' && !lostReason) {
      lostReason = window.prompt(t('crmLostReasonPrompt'));
      if (!lostReason) return;
    }
    axios.patch(process.env.REACT_APP_API_HOST + '/api/v2/crm/workspace/deals/' + deal.id + '/stage', {stageId: stage.id, lostReason}, {headers})
      .then(load).catch(error => window.App.toastShow('error', t('error'), error.toString(), '', error.response));
  };
  const formatMoney = deal => deal.amount ? new Intl.NumberFormat(undefined, {style: 'currency', currency: deal.currency, maximumFractionDigits: deal.currency === 'HUF' ? 0 : 2}).format(Number(deal.amount)) : '—';

  const pipeline = pipelines.find(item => Number(item.id) === Number(pipelineId));
  const stages = (pipeline?.stages || []).filter(item => item.active);
  return <div className="crm-opportunity-board">
    <div className="crm-board-toolbar"><div><h2>{t('crmOpportunities')}</h2><span>{t('crmPipelineHint')}</span></div><div className="p-d-flex p-ai-center p-gap-2"><Dropdown value={pipelineId} options={pipelines} optionLabel="name" optionValue="id" onChange={event => setPipelineId(event.value)} placeholder={t('crmSelectPipeline')}/>{canWrite && <Button label={t('crmAddOpportunity')} icon="pi pi-plus" onClick={() => setDialog({visible: true, dealId: null})}/>}</div></div>
    <div className={'crm-kanban ' + (loading ? 'crm-busy' : '')}>
      {stages.map(stage => {
        const items = deals.filter(deal => Number(deal.pipeline_id) === Number(pipelineId) && Number(deal.stage_id) === Number(stage.id));
        return <section className="crm-kanban-column" key={stage.id} style={{borderTop: `4px solid ${stage.color || '#3B82F6'}`}} onDragOver={event => event.preventDefault()} onDrop={event => {
          const deal = deals.find(item => Number(item.id) === Number(event.dataTransfer.getData('text/plain'))); if (deal) move(deal, stage);
        }}>
          <div className="crm-kanban-title"><span>{stage.name}</span><Tag value={items.length}/></div>
          <div className="crm-kanban-body">{items.map(deal => <article className="crm-deal-card" key={deal.id} draggable={canWrite} onDragStart={event => event.dataTransfer.setData('text/plain', deal.id)} onClick={() => setDialog({visible: true, dealId: deal.id})}>
            <strong>{deal.name}</strong><span>{deal.partner_name || t('crmNoPartner')}</span><b>{formatMoney(deal)}</b><small>{deal.probability != null ? deal.probability + '%' : '—'} · {t('crmNextStep')}: {deal.next_action_at ? formatCrmDateTime(deal.next_action_at, t) : '—'}</small>
          </article>)}</div>
        </section>;
      })}
    </div>
    <Dialog visible={dialog.visible} modal style={{width: 'min(980px, 96vw)'}} header={dialog.dealId ? t('goalUpdate') : t('goalCreate')} onHide={() => setDialog({visible: false, dealId: null})}>
      {dialog.dealId && <div className="p-d-flex p-jc-end p-mb-3 p-gap-2">
        <Button type="button" label={t('crmCreateOffer')} icon="pi pi-file" className="p-button-outlined" onClick={() => {const deal = deals.find(item => Number(item.id) === Number(dialog.dealId)); history.push('/project/offer/create?crmDealId=' + dialog.dealId + '&name=' + encodeURIComponent(deal?.name || '') + '&partnerId=' + (deal?.partner_id || '') + '&partnerName=' + encodeURIComponent(deal?.partner_name || ''));}}/>
        {canWriteTask && <Button type="button" label={t('crmCreateLinkedTask')} icon="pi pi-check-square" className="p-button-outlined" onClick={() => setTaskDialog(true)}/>}</div>}
      <DealFormComponent key={dialog.dealId || 'new'} embedded dealId={dialog.dealId} initialPipelineId={pipelineId} onSaved={() => {setDialog({visible: false, dealId: null}); load();}}/>
    </Dialog>
    <Dialog visible={taskDialog} modal style={{width: 'min(900px, 96vw)'}} header={t('taskCreate')} onHide={() => setTaskDialog(false)}>
      {dialog.dealId && <TaskFormComponent key={'deal-task-' + dialog.dealId} embedded dealId={dialog.dealId}
        partnerId={deals.find(item => item.id === dialog.dealId)?.partner_id || null}
        partnerName={deals.find(item => item.id === dialog.dealId)?.partner_name || ''}
        onSaved={() => setTaskDialog(false)}/>} 
    </Dialog>
  </div>;
};

export default withTranslation('common')(OpportunityKanbanComponent);
